import { FormikConfig, FormikProvider, FormikValues, useFormik } from 'formik';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useRef } from 'react';

interface FormExtraProps {
  validateOnChange?: boolean;
}

type FormikProps = FormikConfig<FormikValues> & FormExtraProps;

type FormProps = Omit<FormikProps, 'validateOnChange' | 'validateOnMount'>;

// This component wraps Formik and provides debounced validation on value changes
// Because formik by default renders multiple times per key stroke when validateOnChange is true
// All other formik functionality is passed through
export const DebouncedFormik: React.FC<FormProps> = ({
  children,
  ...props
}) => {
  const formMethods = useFormik({
    ...props,
    validateOnChange: false,
  });

  const validateForm = formMethods.validateForm;

  // Preserve the ability to use render-props pattern with Formik
  const childs =
    children instanceof Function ? children(formMethods) : children;

  const validate = useCallback(
    (values) => {
      validateForm(values);
    },
    [validateForm]
  );

  useDebouncedValidate({
    validate: validate,
    debounceTime: 300,
    values: formMethods.values,
  });

  return <FormikProvider value={formMethods}>{childs}</FormikProvider>;
};

export function useDebouncedValidate<T extends FormikValues>({
  values,
  validate,
  debounceTime = 300,
}: {
  values: T;
  validate: FormikConfig<T>['validate'];
  debounceTime?: number;
}) {
  const debouncedFunction = useRef(
    debounce((validateFunc: FormikConfig<T>['validate'], data: T) => {
      return validateFunc?.(data);
    }, debounceTime)
  );

  const debounceValidate = useCallback(
    (data) => {
      return debouncedFunction.current(validate, data);
    },
    [validate]
  );

  useEffect(() => {
    debounceValidate(values);
  }, [values, debounceValidate]);
}
